import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, photoUrl } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { Heart, Lock, Star, MapPin, Briefcase, GraduationCap, Sparkles, BadgeCheck, Users, Settings, Info, X, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FACTOR_LABELS = {
  intention: "Ægteskabsintention",
  religion: "Tro & praksis",
  location: "Bopæl & flytning",
  family: "Familie & børn",
  lifestyle: "Livsstil (halal)",
  age: "Alder",
  languages: "Sprog",
  education: "Uddannelse & karriere",
};

export default function MatchFeed() {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [introFor, setIntroFor] = useState(null); // {id, display_name}
  const [introMsg, setIntroMsg] = useState("");
  const [introBusy, setIntroBusy] = useState(false);
  const [breakdownFor, setBreakdownFor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && typeof user === "object" && (user.role === "admin" || user.role === "moderator");

  const load = async () => {
    try {
      const { data } = await api.get("/matches/feed?limit=30&offset=0");
      setState({ loading: false, data, err: "" });
    } catch (e) {
      setState({ loading: false, data: null, err: formatApiError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const loadMore = async () => {
    if (!state.data || !state.data.has_more) return;
    setLoadingMore(true);
    try {
      const offset = (state.data.matches || []).length;
      const { data } = await api.get(`/matches/feed?limit=30&offset=${offset}`);
      setState((s) => ({
        ...s,
        data: { ...data, matches: [...s.data.matches, ...(data.matches || [])] },
      }));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoadingMore(false);
  };

  const sendIntroduction = async () => {
    if (!introFor) return;
    setIntroBusy(true);
    try {
      await api.post("/matches/interests", { to_profile_id: introFor.id, message: introMsg });
      setIntroFor(null);
      setIntroMsg("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIntroBusy(false);
    }
  };

  if (state.loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-brown-500">Indlæser dine forslag…</div>;

  const data = state.data;
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-16 text-destructive">{state.err}</div>;

  // Admins don't have a matching profile — show a friendly "go to admin" card instead of the user-flow gates
  if (isAdmin && (data.viewer_status === "missing" || data.viewer_status === "draft" || data.viewer_status === "pending" || data.viewer_status === "rejected")) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center animate-fade-up" data-testid="feed-admin-shortcut">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/15 text-gold-600 mx-auto">
          <Settings className="h-7 w-7" />
        </div>
        <div className="eyebrow mt-6">Admin-konto</div>
        <h1 className="mt-2 font-serif text-4xl text-olive-700">Du har fuld adgang</h1>
        <p className="mt-4 text-brown-600">
          Admins bruger ikke match-feedet — du styrer platformen fra kontrolpanelet.
        </p>
        <Link to="/admin" className="btn-primary mt-8 inline-flex" data-testid="go-admin-from-feed">
          <Settings className="h-4 w-4" /> Gå til kontrolpanel
        </Link>
      </div>
    );
  }

  if (data.viewer_status === "missing" || data.viewer_status === "draft") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="feed-empty-complete-profile">
        <h1 className="font-serif text-3xl text-olive-700">Udfyld din profil først</h1>
        <p className="mt-4 text-brown-600">Vi viser kun forslag til godkendte medlemmer. Udfyld din profil for at begynde.</p>
        <Link to="/profile" className="btn-primary mt-8 inline-flex" data-testid="go-build-profile">Byg min profil</Link>
      </div>
    );
  }
  if (data.viewer_status === "pending") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="feed-pending-review">
        <h1 className="font-serif text-3xl text-olive-700">Afventer gennemgang</h1>
        <p className="mt-4 text-brown-600">Din profil gennemgås. Vi giver dig besked når den er aktiv.</p>
      </div>
    );
  }
  if (data.viewer_status === "rejected") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-olive-700">Profil endnu ikke godkendt</h1>
        <p className="mt-4 text-brown-600">Redigér din profil og indsend igen.</p>
        <Link to="/profile" className="btn-primary mt-8 inline-flex" data-testid="edit-profile-after-reject">Redigér profil</Link>
      </div>
    );
  }

  const subscribed = data.subscribed;
  const matches = data.matches || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="match-feed-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Udvalgt til dig</div>
          <h1 className="mt-2 font-serif text-4xl text-olive-700">Dine forslag</h1>
          <p className="mt-2 text-brown-600 text-sm max-w-xl">
            Introduktioner rangeres efter kompatibilitet — tro, værdier, familie og livsfase.
          </p>
        </div>
        {!subscribed && (
          <Link to="/pricing" className="btn-primary" data-testid="feed-upgrade-cta">Lås op for fuld adgang</Link>
        )}
      </div>

      {!subscribed && (
        <div className="mt-8 card-soft p-6 flex items-start gap-4 bg-gold-100/60 border-gold-500/30" data-testid="feed-subscription-banner">
          <Lock className="h-5 w-5 text-gold-600 shrink-0 mt-0.5" />
          <div className="text-sm text-brown-600">
            Fotos er slørede og detaljer er begrænsede for gratis medlemmer. Et medlemskab låser op for fulde profiler, sikker samtale og prioriteret placering.
          </div>
        </div>
      )}

      {matches.length === 0 && (
        <div className="mt-12 text-center card-soft p-10" data-testid="feed-no-matches">
          <Sparkles className="h-6 w-6 mx-auto text-gold-500" />
          <h3 className="mt-4 font-serif text-2xl text-olive-700">Ingen forslag endnu</h3>
          <p className="mt-2 text-brown-600 text-sm">Kig forbi snart — vi kuraterer forslag efterhånden som nye profiler godkendes.</p>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {matches.map((m) => (
          <article key={m.id} className="card-soft overflow-hidden hover:shadow-medium hover:-translate-y-1 transition-all" data-testid={`match-card-${m.id}`}>
            <div className="relative aspect-[4/5] bg-sand-200 overflow-hidden">
              {(m.photos && m.photos[0]) ? (
                <img src={photoUrl(m.photos[0])} alt="" loading="lazy" decoding="async" className={`w-full h-full object-cover ${m.photos_blurred ? "blur-photo" : ""}`} />
              ) : m.photos_count > 0 ? (
                <div className={`w-full h-full bg-gradient-to-br from-sand-200 to-sand-300 ${m.photos_blurred ? "blur-photo" : ""}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-brown-500/60 text-sm">Intet foto uploadet</div>
              )}
              {m.photos_blurred && m.photos_count > 0 && (
                <Link
                  to="/pricing"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute inset-0 flex items-end justify-center p-4 bg-gradient-to-t from-black/30 via-transparent to-transparent group/upgrade"
                  data-testid={`feed-upgrade-photo-${m.id}`}>
                  <div className="w-full flex flex-col items-center gap-1.5 rounded-2xl bg-white/95 backdrop-blur-md border border-gold-500/40 px-4 py-2.5 shadow-lg group-hover/upgrade:bg-gold-100/95 transition">
                    <div className="text-[10px] uppercase tracking-widest text-gold-700 flex items-center gap-1"><Lock className="h-3 w-3" /> Premium</div>
                    <div className="text-sm font-serif text-olive-700">Tegn medlemskab for at se fotos</div>
                  </div>
                </Link>
              )}
              <div className="absolute top-3 left-3 bg-white/95 border border-sand-300 rounded-full px-3 py-1 text-xs text-olive-600 flex items-center gap-1">
                <Star className="h-3 w-3 text-gold-500" /> {m.compatibility_score}% match
                {m.compatibility_breakdown && Object.keys(m.compatibility_breakdown).length > 0 && (
                  <button onClick={() => setBreakdownFor(breakdownFor === m.id ? null : m.id)} className="ml-1 text-olive-500 hover:text-olive-700" data-testid={`why-match-${m.id}`}>
                    <Info className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {m.boosted && (
                <div className="absolute top-3 right-3 bg-gold-500 text-white rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest flex items-center gap-1 shadow-soft" data-testid={`boost-badge-${m.id}`}>
                  <Sparkles className="h-3 w-3" /> Boosted
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-serif text-2xl text-olive-700 flex items-center gap-2">
                  {m.display_name}, {m.age}
                  {m.verified && (
                    <span title="Verificeret af Find Your Naseeb" data-testid={`verified-${m.id}`}>
                      <BadgeCheck className="h-5 w-5 text-sky-600 fill-sky-100" />
                    </span>
                  )}
                </h3>
                {m.has_wali && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-olive-100 text-olive-700 px-2 py-0.5 rounded-full border border-olive-500/30" data-testid={`wali-badge-${m.id}`}>
                    <Users className="h-3 w-3" /> Wali involveret
                  </span>
                )}
              </div>
              <div className="mt-2 space-y-1.5 text-sm text-brown-600">
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold-500" /> {m.city}, {m.country}</div>
                {m.profession && <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-gold-500" /> {m.profession}</div>}
                {m.education && <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-gold-500" /> {m.education}</div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="text-xs px-2 py-0.5 rounded-full bg-olive-100 text-olive-600">{m.sect}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gold-100 text-gold-600">{m.religious_practice.replaceAll("_"," ")}</span>
                {(m.values || []).slice(0,2).map((v) => (
                  <span key={v} className="text-xs px-2 py-0.5 rounded-full bg-sand-200 text-brown-600">{v}</span>
                ))}
              </div>
              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => { setIntroFor({ id: m.id, display_name: m.display_name }); setIntroMsg(""); }}
                  className="btn-primary flex-1 text-sm py-2.5 px-4"
                  data-testid={`introduce-${m.id}`}
                >
                  <Heart className="h-4 w-4 fill-rose-500 text-rose-500" /> Introducér
                </button>
                <button
                  onClick={() => navigate(`/matches/${m.id}`)}
                  className="btn-secondary text-sm py-2.5 px-4"
                  data-testid={`view-profile-${m.id}`}
                >
                  Se profil
                </button>
                <button
                  onClick={async () => {
                    if (!confirm("Skjul denne profil?")) return;
                    try { await api.post("/matches/pass", { user_id: m.user_id }); await load(); }
                    catch (e) { toast.error(formatApiError(e)); }
                  }}
                  className="btn-ghost text-sm py-2.5 px-3"
                  title="Skjul fra feed"
                  data-testid={`pass-${m.id}`}
                >
                  <EyeOff className="h-4 w-4" />
                </button>
              </div>
              {breakdownFor === m.id && m.compatibility_breakdown && (
                <div className="mt-4 p-3 rounded-xl bg-sand-100 border border-sand-300" data-testid={`breakdown-${m.id}`}>
                  <div className="text-xs uppercase tracking-widest text-brown-500 mb-2">Hvorfor matcher I?</div>
                  <div className="space-y-1.5">
                    {Object.entries(m.compatibility_breakdown).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <span className="text-brown-600 w-32 shrink-0">{FACTOR_LABELS[k] || k}</span>
                        <div className="flex-1 h-1.5 bg-sand-200 rounded-full overflow-hidden">
                          <div className="h-full bg-olive-500" style={{ width: `${v.score}%` }} />
                        </div>
                        <span className="text-brown-500 w-10 text-right">{v.score}%</span>
                        <span className="text-brown-400 w-8 text-right">·{v.weight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>

      {state.data?.has_more && (
        <div className="mt-8 text-center">
          <button onClick={loadMore} disabled={loadingMore} className="btn-secondary disabled:opacity-60" data-testid="load-more">
            {loadingMore ? "Indlæser…" : "Vis flere forslag"}
          </button>
        </div>
      )}

      {/* Introduction modal */}
      {introFor && (
        <div className="fixed inset-0 z-50 bg-olive-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="intro-modal">
          <div className="bg-white rounded-2xl shadow-soft max-w-lg w-full p-6 sm:p-8 animate-fade-up">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">Introducér dig selv</div>
                <h3 className="mt-1 font-serif text-2xl text-olive-700">Til {introFor.display_name}</h3>
              </div>
              <button onClick={() => setIntroFor(null)} className="text-brown-500 hover:text-olive-700" data-testid="intro-close"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-3 text-sm text-brown-600">
              Skriv et kort, respektfuldt hej. Nævn hvem du er, hvad du søger, og noget I måske deler. Ingen blinde likes — vi vælger intention.
            </p>
            <textarea
              value={introMsg}
              onChange={(e) => setIntroMsg(e.target.value.slice(0, 300))}
              rows={5}
              maxLength={300}
              placeholder="Assalamu alaikum, mit navn er… Jeg søger en seriøs ægteskabspartner og satte pris på…"
              className="field mt-4 w-full"
              data-testid="intro-message"
            />
            <div className="mt-1 text-right text-xs text-brown-500">{introMsg.length}/300</div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={() => setIntroFor(null)} className="btn-secondary text-sm px-4 py-2" data-testid="intro-cancel">Annullér</button>
              <button onClick={sendIntroduction} disabled={introBusy} className="btn-primary text-sm px-5 py-2 disabled:opacity-60" data-testid="intro-send">
                {introBusy ? "Sender…" : "Send introduktion"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
